import { ChangeEvent, Fragment, ReactNode, useEffect, useState } from 'react';

import {
    Button,
    Divider,
    Flex,
    Icon,
    ListView,
    ProgressBar,
    SearchField,
    Text,
} from '@servicetitan/anvil2';
import IconWarning from '@servicetitan/anvil2/assets/icons/material/round/warning.svg';
import { core } from '@servicetitan/anvil2/token';

import { BottomDrawer } from '../bottom-drawer/bottom-drawer';

export type ItemType<T> = T & {
    id: string | number;
    label: string;
    disabled?: boolean;
};

export interface BottomDrawerMultiSelectProps<T> {
    open: boolean;
    onClose: () => void;
    dropdownOptions?: ItemType<T>[];
    isLoading?: boolean;
    values: ItemType<T>[];
    onChange: (value: ItemType<T>[]) => void;
    onSearchQueryChange: (query: string) => void;
    itemToKey: (item: ItemType<T>) => string | number;
    searchPlaceholder?: string;
    getIsItemErrored?: (item: ItemType<T>) => boolean;
    searchInputInfo?: string;
    itemRenderer?: (item: ItemType<T>) => ReactNode;
}

export const BottomDrawerMultiSelect = <T,>(props: BottomDrawerMultiSelectProps<T>) => {
    const {
        dropdownOptions,
        values,
        isLoading = false,
        onChange,
        onSearchQueryChange,
        open,
        onClose,
        searchPlaceholder,
        itemToKey,
        getIsItemErrored,
        searchInputInfo,
        itemRenderer,
    } = props;

    const [selectedItems, setSelectedItems] = useState<ItemType<T>[]>(values ?? []);

    useEffect(() => {
        setSelectedItems(values);
    }, [values]);

    const handleSearchQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
        onSearchQueryChange(e.target.value);
    };

    const handleSearchInputLayout = (inputRef: HTMLInputElement | null) => {
        setTimeout(() => {
            if (inputRef) {
                inputRef.focus();
            }
        }, 0);
    };

    return (
        <BottomDrawer open={open} onClose={onClose} longer>
            <Flex className="h-100 of-y-hidden" direction="column" gap="2">
                <Flex gap="2" className="p-x-2 p-b-2 p-t-half border-bottom" direction="column">
                    <SearchField
                        autoFocus
                        ref={handleSearchInputLayout}
                        placeholder={searchPlaceholder}
                        onChange={handleSearchQueryChange}
                    />
                    <Text subdued size="small">
                        <i>{searchInputInfo ?? 'Begin typing to view matches'}</i>
                    </Text>
                </Flex>

                {isLoading ? (
                    <Flex justifyContent="space-between" alignItems="center" className="m-4">
                        <ProgressBar indeterminate label="" />
                    </Flex>
                ) : dropdownOptions?.length === 0 ? (
                    <Flex alignItems="center" justifyContent="center" grow="1">
                        No Result Found
                    </Flex>
                ) : (
                    <Fragment>
                        <div className="flex-grow-1 of-y-scroll">
                            <ListView
                                items={dropdownOptions ?? []}
                                selected={selectedItems}
                                onSelectionChange={(items: ItemType<T>[]) =>
                                    setSelectedItems(items)
                                }
                            >
                                {({ items }) =>
                                    items.map(item => {
                                        if (getIsItemErrored?.(item)) {
                                            return (
                                                <ListView.Option key={itemToKey(item)} item={item}>
                                                    <Flex
                                                        direction="row"
                                                        alignItems="center"
                                                        flex={1}
                                                    >
                                                        <Flex flex={1}>
                                                            {itemRenderer?.(item) ?? (
                                                                <Text size="small" subdued>
                                                                    {item.label}
                                                                </Text>
                                                            )}
                                                        </Flex>
                                                        <Icon
                                                            color="#E13212"
                                                            svg={IconWarning}
                                                            size="medium"
                                                        />
                                                    </Flex>
                                                </ListView.Option>
                                            );
                                        }
                                        return (
                                            <ListView.Option key={itemToKey(item)} item={item}>
                                                {itemRenderer?.(item) ?? (
                                                    <Text size="small">{item.label}</Text>
                                                )}
                                            </ListView.Option>
                                        );
                                    })
                                }
                            </ListView>
                        </div>

                        <Divider />
                        <Flex
                            justifyContent="flex-end"
                            style={{
                                padding: core.primitive.Size3.value,
                            }}
                        >
                            <Button
                                appearance="primary"
                                size="small"
                                onClick={() => {
                                    onChange(selectedItems);
                                }}
                            >
                                Apply
                            </Button>
                        </Flex>
                    </Fragment>
                )}
            </Flex>
        </BottomDrawer>
    );
};
